import { useState } from "react";
import { RiArrowDownSFill, /*RiArrowDownSLine,*/ RiArrowRightSFill, /*RiArrowUpSFill,*/ RiCloseCircleLine, RiErrorWarningLine, RiInformationLine } from "@remixicon/react";

import Modal, { ModalButtonsSection, ModalContent, ModalSections } from "components/Modal";
import Button from "components/Button";
import { cx } from "utils";
import { PureTooltip } from "components/Tooltip";
import { tooltips } from "./edit-project-components/tooltips";

export type FailureDetail = {
  slug: string;
  level: 'critical' | 'warning' | 'info';
  title: string;
  description?: string;
  numberOfOccurrences: number;
  samples: string[];
  // Guidance links (docs / contact support). Both shown in v1; not call-to-actions.
  docsLink?: {
    label: string;
    url: string;
  };
  supportLink?: {
    label: string;
    url: string;
  };
};

export type FailureDetailsProps = {
  details: FailureDetail[] | undefined;
  projectShouldRunSoon: boolean;
  showCTA: boolean;
  rerunCallback: () => void;
  onClose?: () => void;
};

const LEVEL_ORDER: FailureDetail['level'][] = ['critical', 'warning', 'info'];

const SamplesList = ({ samples, numberOfOccurrences }: { samples: string[]; numberOfOccurrences: number }) => {
  const [expanded, setExpanded] = useState(false);

  if (samples.length === 0) return null;

  return (
    <div>
      <button
        className="flex items-center gap-1 text-sm text-neutral-700 hover:text-neutral-700"
        onClick={() => setExpanded(e => !e)}
      >
        {expanded ? <RiArrowDownSFill className="size-4" /> : <RiArrowRightSFill className="size-4" />}
        {expanded ? 'Hide samples' : `Show samples`}
      </button>
      {expanded && (
        <ul className="mt-2 flex flex-col gap-1">
          {samples.map((sample, i) => (
            <li key={i} className="text-xs text-neutral-600 font-mono truncate">{sample}</li>
          ))}
          {numberOfOccurrences > samples.length && (
            <li className="text-xs text-neutral-400">...</li>
          )}
        </ul>
      )}
    </div>
  );
};

const levelConfig = {
  critical: {
    label: 'Critical',
    cardBg: 'bg-error-50',
    cardBorder: 'border-error-200',
    Icon: RiCloseCircleLine,
    iconClass: 'text-error-500',
  },
  warning: {
    label: 'Warning',
    cardBg: 'bg-warning-50',
    cardBorder: 'border-warning-200',
    Icon: RiErrorWarningLine,
    iconClass: 'text-warning-600',
  },
  info: {
    label: 'Info',
    cardBg: 'bg-info-50',
    cardBorder: 'border-info-200',
    Icon: RiInformationLine,
    iconClass: 'text-info-600',
  },
};

// this modal should be its own route - rather than just a component that renders - stays consistent.
export const FailureDetailsModal = ({ details, projectShouldRunSoon, rerunCallback, onClose, showCTA }: FailureDetailsProps) => {
  if (!details) {
    return null;
  }

  const handleRerun = () => {
    rerunCallback();
    onClose?.();
  };

  return (
    <Modal headline="Failure details" onClose={onClose} className="sm:min-w-[640px]">
      <ModalContent smallerGapFromButtons fullWidthChildren>
        <div className="max-h-[50vh] overflow-y-auto">
        <ModalSections>
          {LEVEL_ORDER.map(level => {
            const levelDetails = details.filter(d => d.level === level);
            if (levelDetails.length === 0) return null;
            const { label, cardBg, cardBorder, Icon, iconClass } = levelConfig[level];
            return (
              <div key={level} className="flex flex-col gap-3">
                <div className="bg-neutral-50 py-2 px-5">
                  <span className="font-semibold text-sm text-neutral-900 whitespace-nowrap">
                    {label}
                  </span>
                </div>
                {levelDetails.map((detail) => (
                  <div
                    key={detail.slug}
                    className={cx("border flex gap-3 p-4 mx-5", cardBg, cardBorder)}
                  >
                    <Icon className={cx("size-6 shrink-0", iconClass)} />
                    <div className="flex flex-1 gap-8 items-start min-w-0">
                      <div className="flex flex-col gap-2 flex-1 min-w-0">
                        <p className="text-base leading-6 text-neutral-900">{detail.title}</p>
                        {detail.description && <p className="text-sm leading-5 text-neutral-700">{detail.description}</p>}
                        <SamplesList samples={detail.samples} numberOfOccurrences={detail.numberOfOccurrences} />
                      </div>
                      {(detail.docsLink || detail.supportLink) && (
                        <div className="shrink-0 flex gap-2 flex-col">
                          {detail.docsLink && (
                            <Button
                              text={detail.docsLink.label}
                              className="button button-secondary"
                              href={detail.docsLink.url}
                              size="SM"
                            />
                          )}
                          {detail.supportLink && (
                            <Button
                              text={detail.supportLink.label}
                              className="button button-secondary"
                              href={detail.supportLink.url}
                              size="SM"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </ModalSections>
        </div>
        <ModalButtonsSection className="mb-4 mx-5">
            <Button
              text="Cancel"
              className="button button-tertiary"
              onClick={onClose}
              size="SM"
            />
            {/* "Re-run Job" calls scheduleNow → a single immediate project run (force-run-once),
                not a retry of this specific job's failed URLs. */}
            {showCTA && (
              <PureTooltip content={projectShouldRunSoon ? tooltips.runNowDisabled : undefined} >
                <Button
                  text="Re-run Job"
                  className="button button-primary"
                  onClick={handleRerun}
                  size="SM"
                  disabled={projectShouldRunSoon}
                />
              </PureTooltip>
            )}
        </ModalButtonsSection>
      </ModalContent>
    </Modal>
  );
};
